import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { ApiResponse } from '../../../interfaces/api-response';
import { Category, SubCategory } from '../../../interfaces/article';
import { ArchiveSearchStateService, InventoryHistoricalSearchState } from '../../../services/archive-search-state.service';
import { AuthService } from '../../../services/auth.service';
import { AuthorizationService } from '../../../services/authorization.service';
import { ConnectServerService } from '../../../services/connect-server.service';
import {
  InventoryHistoricalArticle,
  InventoryHistoricalGroup,
} from '../interfaces/inventory-historical';

@Component({
  selector: 'app-inventory-historical',
  imports: [
    ReactiveFormsModule,
  ],
  templateUrl: './inventory-historical.component.html',
  styleUrl: './inventory-historical.component.scss',
})
export class InventoryHistoricalComponent implements OnInit {

  inventoryHistorical: InventoryHistoricalGroup[] = [];
  hasLoaded = false;

  categoryFilter = new FormControl<number | null>(null);
  subcategoryFilter = new FormControl<number | null>({ value: null, disabled: true });
  caseFilter = new FormControl<string | null>(null);
  rackFilter = new FormControl<string | null>(null);
  shelfFilter = new FormControl<string | null>(null);
  categories: Category[] = [];
  subcategories: SubCategory[] = [];

  private readonly destroyRef = inject(DestroyRef);
  private readonly connectServerService = inject(ConnectServerService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly archiveSearchState = inject(ArchiveSearchStateService);
  private readonly router = inject(Router);
  readonly authService = inject(AuthService);
  readonly canViewFieldsArticle = toSignal(
    this.authorizationService.hasAuthorization('view_fields_historical'),
    { initialValue: false },
  );

  ngOnInit(): void {
    this.categoryFilter.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formLogic();
    });
    this.getCategories();

    const saved = this.archiveSearchState.getInventoryHistorical();
    if (saved) {
      this.restoreFilters(saved);
    } else {
      this.applyFilters();
    }
  }

  get totalArticles(): number {
    return this.inventoryHistorical.reduce((sum, group) => sum + group.listArticles.length, 0);
  }

  get showPlacement(): boolean {
    return this.authService.isLoggedIn() && this.canViewFieldsArticle();
  }

  formLogic(): void {
    this.subcategoryFilter.setValue(null, { emitEvent: false });
    if (this.categoryFilter.value == null) {
      this.subcategoryFilter.disable({ emitEvent: false });
      return;
    }
    this.subcategoryFilter.enable({ emitEvent: false });
  }

  getSubcategories(): void {
    const id = this.categoryFilter.value;
    if (id == null) {
      this.subcategories = [];
      return;
    }
    this.connectServerService.getRequest<ApiResponse<{ subCategories: SubCategory[] }>>
      ('subCategories', { id })
      .subscribe((val: ApiResponse<{ subCategories: SubCategory[] }>) => {
        if (val.data?.subCategories) {
          this.subcategories = val.data.subCategories;
          this.formLogic();
        }
      });
  }

  applyFilters(): void {
    this.persistState();
    this.connectServerService.getRequest<ApiResponse<{ historicalInventory: InventoryHistoricalGroup[] }>>(
      'inventoryHistorical',
      {
        idcategory: this.categoryFilter.value,
        idsubcategory: this.subcategoryFilter.value,
        rack: this.rackFilter.value,
        shelf: this.shelfFilter.value,
        case: this.caseFilter.value,
      },
    ).subscribe((val: ApiResponse<{ historicalInventory: InventoryHistoricalGroup[] }>) => {
      this.inventoryHistorical = (val.data?.historicalInventory ?? []).map((group) => ({
        ...group,
        isOpen: false,
        listArticles: group.listArticles ?? [],
      }));
      this.hasLoaded = true;
    });
  }

  resetFilters(): void {
    this.categoryFilter.setValue(null);
    this.rackFilter.setValue(null);
    this.shelfFilter.setValue(null);
    this.caseFilter.setValue(null);
    this.subcategories = [];
    this.archiveSearchState.clearInventoryHistorical();
    this.applyFilters();
  }

  private getCategories(): void {
    this.connectServerService.getRequest<ApiResponse<{ categories: Category[] }>>('categories', {})
      .subscribe((val: ApiResponse<{ categories: Category[] }>) => {
        this.categories = val.data.categories;
        this.formLogic();
      });
  }

  private restoreFilters(saved: InventoryHistoricalSearchState): void {
    this.rackFilter.setValue(saved.rack, { emitEvent: false });
    this.shelfFilter.setValue(saved.shelf, { emitEvent: false });
    this.caseFilter.setValue(saved.case, { emitEvent: false });
    this.categoryFilter.setValue(saved.idcategory, { emitEvent: false });

    if (saved.idcategory == null) {
      this.subcategoryFilter.disable({ emitEvent: false });
      this.subcategoryFilter.setValue(null, { emitEvent: false });
      this.applyFilters();
      return;
    }

    this.connectServerService.getRequest<ApiResponse<{ subCategories: SubCategory[] }>>(
      'subCategories',
      { id: saved.idcategory },
    ).subscribe((val: ApiResponse<{ subCategories: SubCategory[] }>) => {
      this.subcategories = val.data?.subCategories ?? [];
      this.subcategoryFilter.enable({ emitEvent: false });
      this.subcategoryFilter.setValue(saved.idsubcategory, { emitEvent: false });
      this.applyFilters();
    });
  }

  private persistState(): void {
    this.archiveSearchState.setInventoryHistorical({
      idcategory: this.categoryFilter.value,
      idsubcategory: this.subcategoryFilter.value,
      rack: this.rackFilter.value,
      shelf: this.shelfFilter.value,
      case: this.caseFilter.value,
    });
  }

  toggleGroup(group: InventoryHistoricalGroup): void {
    if (!group.listArticles.length) {
      return;
    }
    group.isOpen = !group.isOpen;
  }

  placement(article: InventoryHistoricalArticle): { label: string; value: string }[] {
    const fields: [string, string | null][] = [
      ['Codice', article.code],
      ['Luogo', article.storage_place],
      ['Piano', article.floor],
      ['Stanza', article.room],
      ['Scaffale', article.rack],
      ['Ripiano', article.shelf],
      ['Contenitore', article.case],
    ];
    return fields
      .filter((field): field is [string, string] => field[1] != null && field[1].trim() !== '')
      .map(([label, value]) => ({ label, value }));
  }

  goToArticle(id: number): void {
    this.persistState();
    this.router.navigate(['archivioStorico/scheda', id]);
  }

}
